import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../shared/utils/api';
import ROUTES from '../../shared/constants/routes';
import { bandTextColor } from './ReadinessRing';
import { computeTrend } from './TrendArrow';

function sliderColor(pct) {
  if (pct === 0) return { fill: '#e5e2db', text: 'var(--text-muted)' };
  if (pct < 40) return { fill: 'var(--sig-red)', text: 'var(--sig-red-text)' };
  if (pct < 70) return { fill: 'var(--sig-amber)', text: 'var(--sig-amber-text)' };
  return { fill: 'var(--sig-green)', text: 'var(--sig-green-text)' };
}

function readinessExplain({ hasPracticeData, hasExternalData, readinessValue }) {
  if (readinessValue == null) {
    return 'Rate your confidence below to get started. Your readiness will improve in accuracy as you practice.';
  }
  if (hasPracticeData || hasExternalData) {
    return 'Based on your confidence rating, practice results, and imported data. Updates automatically as you study.';
  }
  return 'Currently based on your confidence rating only. Practice questions will add objective data and improve accuracy.';
}

function formatLastPractised(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const diffMs = Date.now() - then.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function SubtopicDetailPanel({ topic, onSave }) {
  const navigate = useNavigate();
  const readiness = topic.raw_confidence ?? topic.confidence ?? null;
  const initialConfidence = topic.confidence ?? 0;
  const [confidence, setConfidence] = useState(Math.round(initialConfidence * 100));
  const [notes, setNotes] = useState(topic.notes || '');
  const [practiceStats, setPracticeStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);
  const notesTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (topic.reference_topic_id) {
      api(`/api/topics/${topic.reference_topic_id}/practice-stats`).then(data => {
        if (cancelled || !data || data.error) return;
        setPracticeStats(data);
      }).catch(() => {});
    }
    api(`/api/topics/${topic.id}/history`).then(data => {
      if (cancelled || !Array.isArray(data)) return;
      setHistory(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [topic.id, topic.reference_topic_id]);

  const patchConfidence = useCallback(async (val) => {
    setSaving(true);
    try {
      await api(`/api/topics/${topic.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ confidence: val / 100, is_estimated: false }),
      });
      if (onSave) onSave(topic.id, { confidence: val / 100, is_estimated: false });
      const fresh = await api(`/api/topics/${topic.id}/practice-stats`).catch(() => null);
      if (fresh && !fresh.error && onSave) {
        onSave(topic.id, {
          confidence: fresh.self_rating,
          raw_confidence: fresh.blended_confidence,
          is_estimated: false,
        });
        setPracticeStats(fresh);
      }
    } finally {
      setSaving(false);
    }
  }, [topic.id, onSave]);

  const handleSliderChange = (val) => {
    setConfidence(val);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => patchConfidence(val), 500);
  };

  const handleNotesChange = (val) => {
    setNotes(val);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      await api(`/api/topics/${topic.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: val }),
      });
      if (onSave) onSave(topic.id, { notes: val });
    }, 600);
  };

  const effectiveReadiness = practiceStats?.blended_confidence ?? readiness;
  const readinessPct = effectiveReadiness != null ? Math.round(effectiveReadiness * 100) : null;
  const readinessColor = bandTextColor(effectiveReadiness);
  const sc = sliderColor(confidence);
  const sliderBackground = confidence === 0
    ? '#e5e2db'
    : `linear-gradient(to right, ${sc.fill} ${confidence}%, #e5e2db ${confidence}%)`;

  const sqe = practiceStats?.sqetrack;
  const hasPracticeData = sqe && sqe.questions > 0;
  const external = practiceStats?.external_sources || {};
  const externalProviders = Object.entries(external).filter(([, v]) => v && v.questions > 0);
  const hasExternalData = externalProviders.length > 0;
  const trend = computeTrend(history);

  const explain = readinessExplain({
    hasPracticeData,
    hasExternalData,
    readinessValue: effectiveReadiness,
  });

  const handlePractice = () => {
    navigate(`${ROUTES.QUESTIONS}?mode=topic&topic_id=${topic.id}`);
  };

  const handleAskAlex = () => {
    navigate(`${ROUTES.QUESTIONS}?mode=topic&topic_id=${topic.id}&alex=1`);
  };

  return (
    <div className="tv3-detail-panel">
      {/* Readiness headline */}
      <div className="tv3-readiness-headline">
        <div>
          <div className="tv3-readiness-number" style={{ color: readinessColor }}>
            {readinessPct != null ? `${readinessPct}%` : '—'}
          </div>
          <div className="tv3-readiness-badge" style={{ color: readinessColor }}>Readiness</div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="tv3-readiness-explain">{explain}</div>
          {trend && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
              {trend === 'up' && <span style={{ color: 'var(--sig-green-text)', fontWeight: 600 }}>↑ Trending up</span>}
              {trend === 'down' && <span style={{ color: 'var(--sig-red-text)', fontWeight: 600 }}>↓ Trending down</span>}
              {trend === 'stable' && <span style={{ color: 'var(--text-muted)' }}>→ Holding steady</span>}
            </div>
          )}
        </div>
      </div>

      {/* SQETrack practice stats */}
      {hasPracticeData && (
        <div className="tv3-detail-section">
          <div className="tv3-detail-section-title">SQETrack practice</div>
          <div className="tv3-stat-row">
            <div>
              <div className="tv3-stat-label">Answered</div>
              <div className="tv3-stat-value">{sqe.questions}</div>
            </div>
            <div>
              <div className="tv3-stat-label">Accuracy</div>
              <div className="tv3-stat-value" style={{ color: bandTextColor(sqe.accuracy_pct != null ? sqe.accuracy_pct / 100 : null) }}>
                {sqe.accuracy_pct != null ? `${Math.round(sqe.accuracy_pct)}%` : '—'}
              </div>
            </div>
            <div>
              <div className="tv3-stat-label">Last practised</div>
              <div className="tv3-stat-value">{formatLastPractised(sqe.last_answered)}</div>
            </div>
          </div>
        </div>
      )}

      {/* External data indicator */}
      {hasExternalData && (
        <div className="tv3-external">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          Your readiness includes {externalProviders.map(([n]) => n).join(' and ')} results for this study unit
        </div>
      )}

      {/* Confidence slider */}
      <div className="tv3-detail-section tv3-confidence-section">
        <div className="tv3-detail-section-title">Your confidence</div>
        <div className="tv3-slider-row">
          <span className="tv3-slider-label">How confident do you feel?</span>
          <input
            type="range"
            className="tv3-slider"
            min="0"
            max="100"
            value={confidence}
            onChange={e => handleSliderChange(parseInt(e.target.value, 10))}
            style={{ background: sliderBackground }}
          />
          <span className="tv3-slider-value" style={{ color: sc.text }}>
            {confidence === 0 ? '—' : `${confidence}%`}
          </span>
        </div>
        <p className="tv3-slider-hint">
          {effectiveReadiness == null
            ? 'Move the slider to set your initial confidence.'
            : hasPracticeData || hasExternalData
              ? 'Your self-assessment. One of the inputs to your readiness score.'
              : 'Practice questions will make your readiness score more accurate.'}
        </p>
        {saving && <p className="tv3-slider-hint" style={{ marginTop: 2 }}>Saving…</p>}
      </div>

      {/* Notes */}
      <div>
        <div className="tv3-detail-section-title">Notes</div>
        <textarea
          className="tv3-notes"
          placeholder="Add study notes for this topic..."
          value={notes}
          onChange={e => handleNotesChange(e.target.value)}
        />
      </div>

      {/* Planner status */}
      <div className="tv3-detail-row">
        <span className="tv3-detail-label">Planner</span>
        <span className="tv3-planner-status">Not scheduled</span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="tv3-btn tv3-btn-primary" onClick={handlePractice}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
          Practice this topic
        </button>
        <button className="tv3-btn tv3-btn-secondary" onClick={handleAskAlex}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Ask Alex
        </button>
      </div>
    </div>
  );
}
